import type { Dayjs } from 'dayjs'
import type { MutableRefObject, ReactNode } from 'react'
import React from 'react'

export enum PlayerType {
  DRM = 'drm',
  NO_DRM = 'no_drm',
}

export type AdTagParams = {
  contentScope: string
  callSign: string
  legacyTpMediaId: string
  ppid: string
  latEnabled: boolean
  deviceModel: string
  validPassport: boolean
}

export type VideoPlayerProps = {
  initialPlayheadPosition: number
  adsEnabled: boolean
  loadingAdsFeatureFlag: boolean
}

export type MediaCallType = 'play' | 'stop'

// eslint-disable-next-line @typescript-eslint/naming-convention
export type ga4DataType = {
  video_player: string
  show_title?: string | null
  video_title?: string
  video_tp_media_id?: string
  video_type?: string
  pbs_content_id?: string
  video_streaming_duration?: number
}

export type PlayerEventData = {
  bitrate?: number
  width?: number
  height?: number
  code?: string | number
  message?: string
  data?: any
}

export type AdData = {
  adDuration: number
  adMediaUrl: string
  sourceHeight: number
  sourceWidth: number
  adCurrentTime: number
}

export type UpNextData = {
  isMvod: boolean
  isPlayable: boolean
  upNextShowTitle: string
  upNextEpisodeTitle: string
  upNextEpisodeNumber: number
  upNextSeasonNumber: number
  upNextImage: string
  upNextVideoType: string
  upNextUrl: string
  isUpNextVisible: boolean
}

export type PlayerState = {
  status:
    | 'init'
    | 'loaded'
    | 'playing'
    | 'paused'
    | 'seeking'
    | 'seeked'
    | 'adPlaying'
    | 'adPaused'
    | 'ended'
    | 'exiting'
  adData?: AdData
  currentProgress: number
  duration: number
  upNextData: UpNextData | null
  lastUserInteraction: Dayjs
  isUserTryingToSeek: boolean
  showControls: boolean
}

export type PlayerAction =
  | { type: 'SET_STATUS'; payload: PlayerState['status'] }
  | { type: 'SET_AD_DATA'; payload: AdData | undefined }
  | { type: 'SET_CURRENT_PROGRESS'; payload: number }
  | { type: 'SET_DURATION'; payload: number }
  | { type: 'SET_PLAYER_STATE'; payload: PlayerState }
  | { type: 'UPDATE_PLAYER_STATE'; payload: Partial<PlayerState> }
  | { type: 'SET_AD_CURRENT_TIME'; payload: number }
  | { type: 'SET_UP_NEXT_DATA'; payload: UpNextData | null }
  | { type: 'SET_IS_UP_NEXT_VISIBLE'; payload: boolean }
  | { type: 'SET_LAST_USER_INTERACTION'; payload: Dayjs }
  | { type: 'SET_IS_USER_TRYING_TO_SEEK'; payload: boolean }
  | { type: 'SET_SHOW_CONTROLS'; payload: boolean }

export type VideoPlayerContextType = {
  playerState: PlayerState
  playerStateRef: MutableRefObject<PlayerState>
  dispatch: React.Dispatch<PlayerAction>
  setPlayerStatus: (status: PlayerState['status']) => void
  setAdData: (adData: AdData | undefined) => void
  setCurrentProgress: (progress: number) => void
  setDuration: (duration: number) => void
  updatePlayerState: (updates: Partial<PlayerState>) => void
  setAdCurrentTime: (adCurrentTime: number) => void
  setUpNextData: (upNextData: UpNextData | null) => void
  setIsUpNextVisible: (isUpNextVisible: boolean) => void
  setLastUserInteraction: (lastUserInteraction: Dayjs) => void
  setIsUserTryingToSeek: (isUserTryingToSeek: boolean) => void
  setShowControls: (showControls: boolean) => void
}

export type VideoPlayerProviderProps = {
  children: ReactNode
  initialState?: PlayerState
}
